import React, { useMemo, useState } from 'react';
import { NodeDetailsPanel } from './NodeDetailsPanel';
import { NODE_COLORS } from '../../lib/entityColors';
import type { AccumulatedNode, AccumulatedGraph } from '../../types';
import type { ExpansionSettings } from './GraphExplorerPage';

const TYPE_ORDER = [
  'MalwareFamily',
  'ThreatActor',
  'CVE',
  'IOC',
  'AttackTechnique',
  'Document',
  'WebSource',
];

const TARGET_TYPES = [
  { label: 'MalwareFamily', display: 'Families' },
  { label: 'ThreatActor', display: 'Actors' },
  { label: 'IOC', display: 'IOCs' },
  { label: 'CVE', display: 'CVEs' },
  { label: 'AttackTechnique', display: 'Techniques' },
];

interface GraphSidebarProps {
  selectedNode: AccumulatedNode | null;
  graph: AccumulatedGraph;
  onFocusNode: (nodeId: string) => void;
  onSelectNode: (node: AccumulatedNode) => void;
  onDeselectNode: () => void;
  messages: { id: string; role: string; content: string }[];
  onShowDocs?: (anchorEl: HTMLElement) => void;
  docCount?: number;
  expansionSettings: ExpansionSettings;
  onExpansionSettingsChange: (patch: Partial<ExpansionSettings>) => void;
}

export function GraphSidebar({
  selectedNode,
  graph,
  onFocusNode,
  onSelectNode,
  onDeselectNode,
  messages,
  onShowDocs,
  docCount,
  expansionSettings,
  onExpansionSettingsChange,
}: GraphSidebarProps) {
  const [filter, setFilter] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Group nodes by type
  const grouped = useMemo(() => {
    const lowerFilter = filter.toLowerCase();
    const filtered = lowerFilter
      ? graph.nodes.filter(n => n.name.toLowerCase().includes(lowerFilter))
      : graph.nodes;

    const groups = new Map<string, AccumulatedNode[]>();
    for (const node of filtered) {
      const list = groups.get(node.label) ?? [];
      list.push(node);
      groups.set(node.label, list);
    }

    // Sort groups by TYPE_ORDER, then alphabetically within each group
    return TYPE_ORDER
      .filter(t => groups.has(t))
      .map(t => ({ type: t, nodes: groups.get(t)!.sort((a, b) => a.name.localeCompare(b.name)) }))
      .concat(
        Array.from(groups.entries())
          .filter(([t]) => !TYPE_ORDER.includes(t))
          .map(([t, nodes]) => ({ type: t, nodes: nodes.sort((a, b) => a.name.localeCompare(b.name)) })),
      );
  }, [graph.nodes, filter]);

  const toggleGroup = (type: string) => {
    setCollapsedGroups(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const allSelected = expansionSettings.includeLabels.length === 0;

  const toggleLabel = (label: string) => {
    if (allSelected) {
      const remaining = TARGET_TYPES.map(t => t.label).filter(l => l !== label);
      onExpansionSettingsChange({ includeLabels: remaining });
    } else if (expansionSettings.includeLabels.includes(label)) {
      const next = expansionSettings.includeLabels.filter(l => l !== label);
      onExpansionSettingsChange({ includeLabels: next.length === TARGET_TYPES.length ? [] : next });
    } else {
      const next = [...expansionSettings.includeLabels, label];
      onExpansionSettingsChange({ includeLabels: next.length === TARGET_TYPES.length ? [] : next });
    }
  };

  // ── Node Details mode ─────────────────────────────────────────────────

  if (selectedNode) {
    return (
      <div className="graph-sidebar">
        <div className="graph-sidebar-header">
          <button
            className="graph-sidebar-back-btn"
            onClick={onDeselectNode}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10 3L5 8l5 5" />
            </svg>
            Back to list
          </button>
        </div>
        <div className="graph-sidebar-details">
          <NodeDetailsPanel
            node={selectedNode}
            graph={graph}
            onFocusNode={onFocusNode}
            messages={messages}
            onShowDocs={onShowDocs}
            docCount={docCount}
          />
        </div>
      </div>
    );
  }

  // ── Node List mode ────────────────────────────────────────────────────

  return (
    <div className="graph-sidebar">
      <div className="graph-sidebar-header">
        <span className="graph-sidebar-header-title">Nodes</span>
        <span className="graph-sidebar-header-count">{graph.nodes.length}</span>
      </div>

      <div className="graph-sidebar-filter">
        <input
          className="graph-sidebar-filter-input"
          type="text"
          placeholder="Filter nodes..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>

      <div className="graph-sidebar-node-list">
        {grouped.map(({ type, nodes }) => {
          const collapsed = collapsedGroups[type];
          const color = NODE_COLORS[type] ?? '#aaa';
          return (
            <div key={type} className="graph-sidebar-group">
              <button className="graph-sidebar-group-header" onClick={() => toggleGroup(type)}>
                <span
                  className={`graph-sidebar-group-chevron ${collapsed ? '' : 'open'}`}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                    <path d="M3 2l4 3-4 3z" />
                  </svg>
                </span>
                <span className="graph-sidebar-group-dot" style={{ backgroundColor: color }} />
                <span className="graph-sidebar-group-name">{type}</span>
                <span className="graph-sidebar-group-count">{nodes.length}</span>
              </button>
              {!collapsed && (
                <div className="graph-sidebar-group-items">
                  {nodes.map(node => (
                    <button
                      key={node.id}
                      className="graph-sidebar-node-item"
                      onClick={() => {
                        onSelectNode(node);
                        onFocusNode(node.id);
                      }}
                      title={node.name}
                    >
                      <span className="graph-sidebar-node-dot" style={{ backgroundColor: color }} />
                      <span className="graph-sidebar-node-name">{node.name}</span>
                      {node.score != null && node.score > 0 && (
                        <span className="graph-sidebar-node-score">{node.score}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Expansion Settings */}
      <div className="graph-sidebar-settings-section">
        <button
          className="graph-sidebar-settings-header"
          onClick={() => setSettingsOpen(v => !v)}
        >
          <span className={`graph-sidebar-group-chevron ${settingsOpen ? 'open' : ''}`}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              <path d="M3 2l4 3-4 3z" />
            </svg>
          </span>
          Expansion Settings
        </button>
        {settingsOpen && (
          <div className="graph-sidebar-settings-body">
            {/* Depth */}
            <div className="expansion-settings-section">
              <div className="expansion-settings-label">Depth</div>
              <div className="expansion-settings-toggle-group">
                <button
                  className={`expansion-settings-toggle ${expansionSettings.depth === 1 ? 'active' : ''}`}
                  onClick={() => onExpansionSettingsChange({ depth: 1 })}
                >
                  1 hop
                </button>
                <button
                  className={`expansion-settings-toggle ${expansionSettings.depth === 2 ? 'active' : ''}`}
                  onClick={() => onExpansionSettingsChange({ depth: 2 })}
                >
                  2 hops
                </button>
              </div>
            </div>

            {/* Skip documents */}
            <div className="expansion-settings-section">
              <label className="expansion-settings-checkbox-label">
                <input
                  type="checkbox"
                  checked={expansionSettings.excludeDocuments}
                  onChange={e => onExpansionSettingsChange({ excludeDocuments: e.target.checked })}
                />
                Skip documents
              </label>
            </div>

            {/* Target types */}
            <div className="expansion-settings-section">
              <div className="expansion-settings-label">
                Expand types
                <button
                  className="expansion-settings-all-btn"
                  onClick={() => onExpansionSettingsChange({ includeLabels: [] })}
                >
                  {allSelected ? 'All' : 'Reset'}
                </button>
              </div>
              <div className="expansion-settings-type-grid">
                {TARGET_TYPES.map(({ label, display }) => (
                  <label key={label} className="expansion-settings-checkbox-label">
                    <input
                      type="checkbox"
                      checked={allSelected || expansionSettings.includeLabels.includes(label)}
                      onChange={() => toggleLabel(label)}
                    />
                    {display}
                  </label>
                ))}
              </div>
            </div>

            {/* Max neighbors */}
            <div className="expansion-settings-section">
              <div className="expansion-settings-label">Max neighbors</div>
              <div className="expansion-settings-toggle-group">
                {[10, 20, 50].map(n => (
                  <button
                    key={n}
                    className={`expansion-settings-toggle ${expansionSettings.maxNeighbors === n ? 'active' : ''}`}
                    onClick={() => onExpansionSettingsChange({ maxNeighbors: n })}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
